import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import MovieCard from "@/components/feature/MovieCard";
import SectionHeading from "@/components/feature/SectionHeading";
import { searchMovies } from "@/utils/movieUtils";
import { hotMovies } from "@/mocks/movies";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const [keyword, setKeyword] = useState(query);

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  useDocumentTitle({
    title: query ? `搜索“${query}”-屁屁影院` : "影视搜索-屁屁影院",
    description: `在屁屁影院搜索影视作品${query ? `，关键词：${query}` : ""}。快速找到你想要的电影与电视剧。`,
    keywords: "屁屁影院,影视搜索,电影搜索,电视剧搜索",
  });

  const results = useMemo(() => searchMovies(query), [query]);
  const hot = useMemo(() => hotMovies.slice(0, 6), []);
  const searched = query.trim().length > 0;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const kw = keyword.trim();
    if (kw) setSearchParams({ q: kw });
    else setSearchParams({});
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main className="px-4 py-8 md:px-6 md:py-10">
        <div className="mx-auto max-w-[1280px]">
          <h1 className="text-2xl font-bold text-foreground-950 md:text-3xl">影视搜索</h1>
          <p className="mt-2 text-sm text-foreground-500">输入片名、导演、演员或类型，快速找到你想看的内容。</p>

          <form onSubmit={handleSubmit} className="mt-6 flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-foreground-400" />
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索电影、电视剧、导演、演员"
                aria-label="搜索关键词"
                className="w-full rounded-full border border-background-200 bg-background-100 py-3 pl-11 pr-11 text-sm text-foreground-950 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
              />
              {keyword && (
                <button
                  type="button"
                  onClick={() => setKeyword("")}
                  aria-label="清空关键词"
                  className="absolute right-3 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full text-foreground-400 hover:bg-background-200 hover:text-foreground-950"
                >
                  <i className="ri-close-line" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="whitespace-nowrap rounded-full bg-accent-500 px-8 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
            >
              搜索
            </button>
          </form>

          {searched && (
            <p className="mt-5 text-sm text-foreground-500">
              关键词 <strong className="text-accent-400">“{query}”</strong> 共找到{" "}
              <strong className="text-accent-400">{results.length}</strong> 个结果
            </p>
          )}

          {searched && results.length > 0 && (
            <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-5">
              {results.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          )}

          {searched && results.length === 0 && (
            <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-background-200/60 bg-background-100 py-16 text-center">
              <span className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-400">
                <i className="ri-emotion-unhappy-line text-3xl" />
              </span>
              <h2 className="text-lg font-semibold text-foreground-950">没有找到“{query}”相关的内容</h2>
              <p className="mt-2 text-sm text-foreground-500">换个关键词试试，或看看下面的热门影视吧。</p>
            </div>
          )}

          {(!searched || results.length === 0) && (
            <section className="mt-10">
              <SectionHeading title="热门推荐" subtitle="大家都在看的高人气作品" icon="ri-fire-line" />
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-6">
                {hot.map((movie) => (
                  <MovieCard key={movie.id} movie={movie} />
                ))}
              </div>
              <div className="mt-6">
                <Link to="/" className="text-sm text-accent-400 hover:text-accent-300">
                  返回首页浏览全部影视 →
                </Link>
              </div>
            </section>
          )}
        </div>
      </main>

      <SiteFooter />
    </div>
  );
}